import { Check, ChevronRight, Copy, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";

import { useListMedicalCodesByEncounter } from "@elation/api-client/src/stitch-web";

import {
  MEDICAL_CODE_GROUPS,
  MedicalCodeEntity,
  type MedicalCodeGroup,
} from "@src/features/medical-codes";
import { Button } from "@src/shared/components/ui/button";
import { CollapsiblePanel } from "@src/shared/components/v2/CollapsiblePanel";
import { GeneratedArtifactPanelState } from "@src/shared/components/v2/GeneratedArtifactPanelState";
import type { GeneratedArtifactViewState } from "@src/shared/domain/generated-artifact";
import { cn } from "@src/shared/lib/utils";

export interface CompletedVisitMedicalCodeV2 {
  id: string;
  code: string;
  description?: string | null;
  group: MedicalCodeGroup;
  groupLabel: string;
  evidence: string[];
}

export interface CompletedVisitCodesPanelProps {
  encounterId?: number;
  compact?: boolean;
}

export interface CompletedVisitCodesPanelBodyProps {
  codes: CompletedVisitMedicalCodeV2[];
  viewState?: GeneratedArtifactViewState;
  isStillGenerating?: boolean;
  compact?: boolean;
}

export interface CompletedVisitCodesCollapsiblePanelProps {
  encounterId?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  compact?: boolean;
  /** Extra header buttons, for example the dock control. */
  actions?: React.ReactNode;
}

function codesClipboardText(codes: CompletedVisitMedicalCodeV2[]): string {
  return codes
    .map(
      (code) => `${code.groupLabel}\n${code.code}  ${code.description ?? ""}`,
    )
    .join("\n\n");
}

/**
 * Puts every code on the clipboard so a clinician can paste the set into the
 * chart. Both the standalone and the collapsible codes panel show it.
 */
function CopyCodesButton({ codes }: { codes: CompletedVisitMedicalCodeV2[] }) {
  const [copied, setCopied] = useState(false);

  const copyCodes = async () => {
    await navigator.clipboard.writeText(codesClipboardText(codes));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 shrink-0 rounded-md text-muted-foreground"
      aria-label={copied ? "Copied codes" : "Copy codes"}
      disabled={codes.length === 0}
      onClick={copyCodes}
    >
      {copied ? (
        <Check className="size-3.5 text-success" aria-hidden />
      ) : (
        <Copy className="size-3.5" aria-hidden />
      )}
    </Button>
  );
}

function useCompletedVisitMedicalCodes({
  encounterId,
}: {
  encounterId?: number;
}) {
  const medicalCodesQuery = useListMedicalCodesByEncounter(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
      refetchInterval: (query) => {
        return MedicalCodeEntity.medicalCodesPollIntervalMs(
          query.state.data?.data,
        );
      },
    },
  });
  const response = medicalCodesQuery.data?.data;
  const codingStatus = MedicalCodeEntity.codingStatus(response);
  const codes = useMemo<CompletedVisitMedicalCodeV2[]>(() => {
    if (!response) return [];

    const groups = MedicalCodeEntity.groupRows(response);

    return MEDICAL_CODE_GROUPS.flatMap((group) =>
      groups[group].map((row) => ({
        id: `${group}-${row.code}`,
        code: MedicalCodeEntity.codeLabel(row) ?? row.code,
        description: row.description,
        group,
        groupLabel: MedicalCodeEntity.groupLabel(group),
        evidence: MedicalCodeEntity.evidenceDisplayLines(row),
      })),
    );
  }, [response]);

  const viewState = MedicalCodeEntity.resolveViewState({
    codingStatus,
    itemCount: codes.length,
    isInitialLoading: medicalCodesQuery.isLoading,
    isFetchError: medicalCodesQuery.isError,
  });
  const isStillGenerating = MedicalCodeEntity.isStillGenerating({
    codingStatus,
    itemCount: codes.length,
  });

  return {
    codes,
    viewState,
    isStillGenerating,
  };
}

export function CompletedVisitCodesPanel({
  encounterId,
  compact = false,
}: CompletedVisitCodesPanelProps) {
  const { codes, viewState, isStillGenerating } = useCompletedVisitMedicalCodes(
    {
      encounterId,
    },
  );

  return (
    <CompletedVisitCodesFrame
      codes={codes}
      isStillGenerating={isStillGenerating}
    >
      <CompletedVisitCodesPanelBody
        codes={codes}
        viewState={viewState}
        isStillGenerating={isStillGenerating}
        compact={compact}
      />
    </CompletedVisitCodesFrame>
  );
}

export function CompletedVisitCodesCollapsiblePanel({
  encounterId,
  open,
  defaultOpen = true,
  onOpenChange,
  compact = false,
  actions,
}: CompletedVisitCodesCollapsiblePanelProps) {
  const { codes, viewState, isStillGenerating } = useCompletedVisitMedicalCodes(
    {
      encounterId,
    },
  );

  return (
    <CollapsiblePanel
      label="Codes"
      count={String(codes.length)}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className="h-full"
      contentClassName="min-h-0 overflow-x-hidden overflow-y-auto"
      actions={
        <>
          {actions}
          <CopyCodesButton codes={codes} />
        </>
      }
    >
      <CompletedVisitCodesPanelBody
        codes={codes}
        viewState={viewState}
        isStillGenerating={isStillGenerating}
        compact={compact}
      />
    </CollapsiblePanel>
  );
}

function CompletedVisitCodesFrame({
  codes,
  isStillGenerating = false,
  children,
}: {
  codes: CompletedVisitMedicalCodeV2[];
  isStillGenerating?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[10px] border border-border bg-background">
      <div className="flex min-h-10 items-center justify-between gap-2 border-border border-b bg-surface px-3 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="size-3.5 shrink-0 text-primary" aria-hidden />
          <span className="font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Codes
          </span>
          <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {codes.length}
          </span>
          {isStillGenerating ? (
            <span
              role="status"
              aria-live="polite"
              className="truncate text-[11px] text-muted-foreground"
            >
              Still checking for more…
            </span>
          ) : null}
        </div>
        <CopyCodesButton codes={codes} />
      </div>
      {children}
    </div>
  );
}

export function CompletedVisitCodesPanelBody({
  codes,
  viewState = "ready",
  isStillGenerating = false,
  compact = false,
}: CompletedVisitCodesPanelBodyProps) {
  const [openCodeId, setOpenCodeId] = useState<string | null>(
    codes[0]?.id ?? null,
  );
  const groupedCodes = useMemo(() => {
    const groups = new Map<MedicalCodeGroup, CompletedVisitMedicalCodeV2[]>();
    for (const code of codes) {
      const group = groups.get(code.group) ?? [];
      group.push(code);
      groups.set(code.group, group);
    }
    return Array.from(groups.entries());
  }, [codes]);

  if (openCodeId == null && codes.length > 0) {
    setOpenCodeId(codes[0].id);
  }

  return (
    <GeneratedArtifactPanelState
      viewState={
        viewState === "ready" && groupedCodes.length === 0 ? "empty" : viewState
      }
      isStillGenerating={isStillGenerating}
      generatingLabel="Generating codes"
      emptyMessage="No codes were generated for this visit."
      failedMessage="Coding unavailable for this visit."
      errorFallback="Unable to load codes."
    >
      <div className="min-w-0 divide-y divide-border">
        {groupedCodes.map(([group, groupCodes]) => (
          <section key={group} className="min-w-0">
            <div className="flex items-center justify-between bg-surface px-3 py-2">
              <h3 className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                {MedicalCodeEntity.groupSectionTitle(group)}
              </h3>
              <span className="font-mono text-[10px] text-muted-foreground">
                {groupCodes.length}
              </span>
            </div>
            <div className="min-w-0 divide-y divide-border">
              {groupCodes.map((code) => {
                const isOpen = openCodeId === code.id;
                const evidence = code.evidence?.filter(Boolean) ?? [];

                return (
                  <div key={code.id} className="min-w-0">
                    <Button
                      type="button"
                      variant="ghost"
                      className={cn(
                        "flex h-auto min-w-0 w-full items-start justify-start gap-3 whitespace-normal rounded-none px-3 py-3 text-left hover:bg-muted/40",
                        compact && "gap-2 px-2.5 py-2",
                      )}
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpenCodeId((current) =>
                          current === code.id ? null : code.id,
                        )
                      }
                    >
                      <span
                        className={cn(
                          "w-16 shrink-0 font-mono text-[12.5px] font-semibold text-foreground",
                          compact && "w-14 text-[11.5px]",
                        )}
                      >
                        {code.code}
                      </span>
                      <span
                        className={cn(
                          "min-w-0 flex-1 break-words text-[13px] font-normal leading-snug text-muted-foreground",
                          compact && "text-[12px]",
                        )}
                      >
                        {MedicalCodeEntity.descriptionLabel(code)}
                      </span>
                      <ChevronRight
                        className={cn(
                          "mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform",
                          isOpen && "rotate-90",
                        )}
                        aria-hidden
                      />
                    </Button>

                    {isOpen ? (
                      <div className="min-w-0 px-3 pb-3">
                        <div className="min-w-0 break-words rounded-md border border-primary/15 bg-primary/5 px-3 py-2 text-[12px] leading-relaxed">
                          <span className="mr-2 font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-primary">
                            From visit
                          </span>
                          <span className="italic text-foreground">
                            {evidence.length > 0
                              ? `"${evidence[0]}"`
                              : "No evidence attached to this code yet."}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </GeneratedArtifactPanelState>
  );
}
